'use client'

import { useState } from 'react'
import { Button, Card, Divider, Icon, Spacer } from '@vinted/web-ui'
import { LocationArrow16, Minus16, Plus16 } from '@vinted/monochrome-icons'
import { noop } from 'lodash'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { CoordinatesModel } from '@marketplace-web/shipping/checkout-shipping-data'

import { getLocationCoordinates } from '../../../../utils/location-utils'

type Props = {
  onZoomIn: () => void
  onZoomOut: () => void
  onLocationResponse: (coordinates: CoordinatesModel) => void
}

const Controls = ({ onZoomIn = noop, onZoomOut = noop, onLocationResponse = noop }: Props) => {
  const translate = useTranslate('checkout.shipping.pickup_point_modal.map')

  const [isLocationLoading, setIsLocationLoading] = useState(false)

  const handleLocationRequest = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isLocationLoading) {
      event.preventDefault()

      return
    }

    try {
      setIsLocationLoading(true)

      const currentPositionCoordinates = await getLocationCoordinates()

      if (!currentPositionCoordinates) return

      onLocationResponse(currentPositionCoordinates)
    } finally {
      setIsLocationLoading(false)
    }
  }

  return (
    <div className="pickup-point-map__controls">
      <Card styling="lifted">
        <Button
          size="small"
          icon={
            <Icon
              name={Plus16}
              color="greyscale-level-2"
              aria={{
                'aria-label': translate('a11y.zoom_in'),
              }}
            />
          }
          onClick={onZoomIn}
          styling="flat"
          testId="map-controls-zoom-in"
        />
        <Divider />
        <Button
          size="small"
          icon={
            <Icon
              name={Minus16}
              color="greyscale-level-2"
              aria={{
                'aria-label': translate('a11y.zoom_out'),
              }}
            />
          }
          onClick={onZoomOut}
          styling="flat"
          testId="map-controls-zoom-out"
        />
      </Card>
      <Spacer />
      <Card styling="lifted">
        <Button
          size="medium"
          icon={
            <Icon
              name={LocationArrow16}
              color="primary-default"
              aria={{
                'aria-label': translate('a11y.location'),
              }}
            />
          }
          onClick={handleLocationRequest}
          theme="primary"
          styling="flat"
          isLoading={isLocationLoading}
          testId={`map-controls-location${isLocationLoading ? '-loading' : ''}`}
        />
      </Card>
    </div>
  )
}

export default Controls
